import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  it: {
    // Navigation
    nav: {
      home: 'Home',
      guides: 'Guide',
      blog: 'Blog',
      newsletter: 'Newsletter',
      buy: 'Acquista'
    },
    // Hero
    hero: {
      title: 'Viaggia.',
      subtitle: 'Scopri.',
      tagline: 'Vivi.',
      description: 'Guide di viaggio curate per esploratori moderni. Ogni destinazione, un\'esperienza unica.',
      cta: 'Esplora le Guide',
      secondary: 'Leggi il Blog'
    },
    // Guides
    guides: {
      title: 'Le Nostre Guide',
      subtitle: 'Curate con passione per viaggiatori esigenti',
      viewAll: 'Vedi Tutte',
      buyNow: 'Acquista Ora',
      featured: 'In Evidenza',
      from: 'Da'
    },
    // Blog
    blog: {
      title: 'Dal Blog',
      subtitle: 'Storie, consigli e ispirazioni di viaggio',
      readMore: 'Leggi',
      viewAll: 'Tutti gli Articoli',
      search: 'Cerca articoli...',
      categories: 'Categorie',
      all: 'Tutti',
      tips: 'Consigli',
      destinations: 'Destinazioni',
      food: 'Cibo'
    },
    // Newsletter
    newsletter: {
      title: 'Resta Connesso',
      subtitle: 'Iscriviti alla newsletter per ricevere consigli di viaggio esclusivi e anteprime delle nuove guide.',
      placeholder: 'La tua email',
      button: 'Iscriviti',
      success: 'Iscrizione completata!',
      error: 'Errore. Riprova.',
      exists: 'Email già iscritta'
    },
    // Footer
    footer: {
      rights: 'Tutti i diritti riservati',
      privacy: 'Privacy',
      terms: 'Termini',
      contact: 'Contatti'
    },
    // Payment
    payment: {
      success: 'Pagamento Completato',
      successDesc: 'Grazie per il tuo acquisto! Riceverai la guida via email.',
      cancel: 'Pagamento Annullato',
      cancelDesc: 'Il pagamento è stato annullato. Puoi riprovare quando vuoi.',
      backHome: 'Torna alla Home',
      processing: 'Verifica pagamento in corso...'
    },
    // Guide Detail
    guideDetail: {
      whatsInside: 'Cosa Include',
      features: [
        'Itinerari dettagliati giorno per giorno',
        'Mappe interattive offline',
        'Consigli su ristoranti e alloggi',
        'Tips per risparmiare',
        'Frasi utili nella lingua locale'
      ],
      buyGuide: 'Acquista la Guida',
      secure: 'Pagamento sicuro con Stripe'
    }
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      guides: 'Guides',
      blog: 'Blog',
      newsletter: 'Newsletter',
      buy: 'Buy'
    },
    // Hero
    hero: {
      title: 'Travel.',
      subtitle: 'Discover.',
      tagline: 'Live.',
      description: 'Curated travel guides for modern explorers. Every destination, a unique experience.',
      cta: 'Explore Guides',
      secondary: 'Read the Blog'
    },
    // Guides
    guides: {
      title: 'Our Guides',
      subtitle: 'Curated with passion for discerning travelers',
      viewAll: 'View All',
      buyNow: 'Buy Now',
      featured: 'Featured',
      from: 'From'
    },
    // Blog
    blog: {
      title: 'From the Blog',
      subtitle: 'Stories, tips and travel inspiration',
      readMore: 'Read',
      viewAll: 'All Articles',
      search: 'Search articles...',
      categories: 'Categories',
      all: 'All',
      tips: 'Tips',
      destinations: 'Destinations',
      food: 'Food'
    },
    // Newsletter
    newsletter: {
      title: 'Stay Connected',
      subtitle: 'Subscribe to our newsletter to receive exclusive travel tips and previews of new guides.',
      placeholder: 'Your email',
      button: 'Subscribe',
      success: 'Successfully subscribed!',
      error: 'Error. Please try again.',
      exists: 'Email already subscribed'
    },
    // Footer
    footer: {
      rights: 'All rights reserved',
      privacy: 'Privacy',
      terms: 'Terms',
      contact: 'Contact'
    },
    // Payment
    payment: {
      success: 'Payment Completed',
      successDesc: 'Thank you for your purchase! You will receive the guide via email.',
      cancel: 'Payment Cancelled',
      cancelDesc: 'Payment was cancelled. You can try again anytime.',
      backHome: 'Back to Home',
      processing: 'Verifying payment...'
    },
    // Guide Detail
    guideDetail: {
      whatsInside: 'What\'s Inside',
      features: [
        'Detailed day-by-day itineraries',
        'Offline interactive maps',
        'Restaurant and accommodation tips',
        'Money-saving tips',
        'Useful phrases in local language'
      ],
      buyGuide: 'Buy the Guide',
      secure: 'Secure payment with Stripe'
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('huacayaa-language');
    return saved || 'it';
  });

  useEffect(() => {
    localStorage.setItem('huacayaa-language', language);
  }, [language]);

  const t = translations[language];

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'it' ? 'en' : 'it');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
